import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { api } from '@/lib/api';
import type { StudyDocument } from './studyApi';
import type { StudySession } from './studyApi';

export type NoteCrop = { top: number; right: number; bottom: number; left: number };
export type NotePage = {
  document_id: string;
  rotation: 0 | 90 | 180 | 270;
  crop?: NoteCrop;
  mime_type?: string;
};
export type StudyNote = {
  id: string;
  title: string;
  body: string;
  topic: string | null;
  subject_key: string | null;
  term_id: string | null;
  class_date: string | null;
  pages: NotePage[];
  version: number;
  status: 'active' | 'archived';
  available: boolean;
  updated_at: string;
  linked_task_ids?: string[];
  linked_tasks?: Array<{
    id: string;
    title: string;
    status: string;
    term_id: string | null;
    due_on: string | null;
  }>;
  linked_artifact_ids?: string[];
  linked_sources?: Array<{ artifact_id: string; document_id: string }>;
  reviews?: Array<{
    task_id: string;
    title: string;
    due_on: string | null;
    term_id: string | null;
    notebook_version: number;
    status: string;
  }>;
};
export type NoteWrite = Omit<
  StudyNote,
  'available' | 'updated_at' | 'version' | 'status' | 'reviews' | 'linked_sources' | 'linked_tasks'
> & {
  expected_version: number;
  request_id: string;
  status: 'active' | 'archived' | 'deleted';
};
export const listStudyNotes = (query: {
  search?: string;
  term_id?: string;
  subject_key?: string;
  document_id?: string;
  topic?: string;
  task_id?: string;
  status?: string;
  cursor?: string;
}) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value) params.set(key, value);
  return api<{ items: StudyNote[]; next_cursor: string | null }>(`/study/notes?${params}`);
};
export const readStudyNote = (id: string) =>
  api<StudyNote>(`/study/notes/${encodeURIComponent(id)}`);
export interface StudyNoteVersion {
  version: number;
  provenance: string;
  created_at: string;
  available: boolean;
  content: NoteWrite | null;
}
export const readStudyNoteVersions = (id: string) =>
  api<{ items: StudyNoteVersion[]; has_more: boolean }>(
    `/study/notes/${encodeURIComponent(id)}/versions`,
  );
export const saveStudyNote = (input: NoteWrite) =>
  api<{ id: string; version: number }>('/study/notes', {
    method: 'POST',
    body: {
      ...input,
      pages: input.pages.map(({ document_id, rotation, crop }) => ({
        document_id,
        rotation,
        ...(crop ? { crop } : {}),
      })),
    },
  });
export type NoteReviewWrite = {
  request_id: string;
  expected_version: number;
  title: string;
  due_on: string | null;
  term_id: string | null;
  subject_key: string | null;
};
export const createNoteReview = (id: string, input: NoteReviewWrite) =>
  api<{ task_id: string }>(`/study/notes/${encodeURIComponent(id)}/reviews`, {
    method: 'POST',
    body: input,
  });

export type NoteTutorSelection = {
  expected_version: number;
  document_id: string;
  goal: 'understand' | 'homework' | 'revise';
  text_from: number | null;
  text_to: number | null;
};
export type NoteTutorStart = { request_id: string; selection: NoteTutorSelection };
export type NoteTutorPreview = {
  notebook_id: string;
  notebook_version: number;
  document_id: string;
  page_number: number;
  learner_text: string;
  truncated?: boolean;
  provenance: 'learner_authored';
  subject: { subject_key: string; display_name: string };
  tutor: NonNullable<StudySession['subject_tutor']>;
  annotations_included: false;
  other_pages_included: false;
};
export const previewNoteTutor = (id: string, selection: NoteTutorSelection) =>
  api<NoteTutorPreview>(`/study/notes/${encodeURIComponent(id)}/tutor-preview`, {
    method: 'POST',
    body: selection,
  });
export const startNoteTutor = (id: string, input: NoteTutorStart) =>
  api<StudySession>(`/study/notes/${encodeURIComponent(id)}/tutor-sessions`, {
    method: 'POST',
    body: input,
  });

type UploadIntent = { url: string; method: 'PUT'; headers: Record<string, string>; s3_key: string };
export type NoteUploadState = {
  signed?: UploadIntent;
  uploaded?: boolean;
  needsVerification?: boolean;
  document?: StudyDocument;
};
// Keep the S3 identity through a lost completion response. Registering the same
// identity is idempotent in StudyService.completeUpload; never upload a new copy.
export async function uploadNotePhoto(file: File, state: NoteUploadState): Promise<StudyDocument> {
  if (state.document) return state.document;
  if (isLocalStudyExperience()) {
    state.document = await api<StudyDocument>('/study/experience/documents', {
      method: 'POST',
      body: { file },
    });
    return state.document;
  }
  const metadata = { file_name: file.name, mime_type: file.type, size_bytes: file.size };
  state.signed ??= await api<UploadIntent>('/study/documents/upload-intent', {
    method: 'POST',
    body: metadata,
  });
  if (!state.uploaded && !state.needsVerification) {
    let response: Response;
    try {
      response = await fetch(state.signed.url, {
        method: state.signed.method,
        headers: state.signed.headers,
        body: file,
      });
    } catch {
      state.needsVerification = true;
      throw new Error('Upload result unknown. Retry this page to verify the original.');
    }
    if (response.status >= 500) {
      state.needsVerification = true;
      throw new Error('Upload result unknown. Retry this page to verify the original.');
    }
    // Immutable PUT may have succeeded before its reply was lost. The backend
    // verifies the original key and exact size; do not mint another object.
    if (response.status === 412) state.needsVerification = true;
    else if (!response.ok) {
      state.signed = undefined;
      throw new Error('Photo upload failed. Retry this page.');
    } else state.uploaded = true;
  }
  try {
    state.document = await api<StudyDocument>('/study/documents/complete', {
      method: 'POST',
      body: { ...metadata, s3_key: state.signed.s3_key, subject_key: null },
    });
    return state.document;
  } catch (error) {
    if (
      error &&
      typeof error === 'object' &&
      'code' in error &&
      error.code === 'STUDY_UPLOAD_INCOMPLETE'
    ) {
      state.signed = undefined;
      state.uploaded = false;
      state.needsVerification = false;
    }
    throw error;
  }
}

export type NoteMap = {
  id: string;
  document_id: string;
  page_number: number;
  revision: number;
  status: string;
  provenance: string;
  map: {
    nodes: Array<{ id: string; label: string; notes: string }>;
    edges: Array<{ from: string; to: string }>;
  } | null;
};
export type NoteMaps = {
  eligible: NoteMap[];
  linked: NoteMap[];
  truncated: boolean;
  unavailable_links: number;
};
export const getNoteMaps = (id: string) =>
  api<NoteMaps>(`/study/notes/${encodeURIComponent(id)}/maps`);
